import { AsyncSelect, type IAsyncSelectProps } from '@procurify/ui'
import { useSelector } from 'react-redux'

import { ContentTypeAppLabels } from '@webapp/core/entities/internal/enums/ContentTypeAppLabelEnum'
import { ContentTypeModels } from '@webapp/core/entities/internal/enums/ContentTypeModelEnum'
import { makeSelectContentTypesByModel } from '@webapp/core/stores/global'
import { useProcurifyIntl } from '@webapp/hooks'
import { approverRepository } from '@webapp/modules/expense/data-access/repositories/ApproverRepository'
import { RequestableApproverOption } from '@webapp/modules/expense/entities/RequestableApproverOption'
import messages from './messages'

export type NextApproverSelectValue = RequestableApproverOption

export interface INextApproverSelectProps extends IAsyncSelectProps<NextApproverSelectValue> {
  departmentId: Nullable<number>
  accountCodeIds: number[]
  amount: number
}

export const NextApproverSelect = ({
  departmentId,
  accountCodeIds,
  amount,
  ...props
}: INextApproverSelectProps) => {
  const intl = useProcurifyIntl()

  const expenseContentType = useSelector(
    makeSelectContentTypesByModel(
      ContentTypeAppLabels.PROCURIFY,
      ContentTypeModels.EXPENSEREPORT
    )
  )
  const baseContentTypeId = expenseContentType?.id

  const loadOptions: IAsyncSelectProps<NextApproverSelectValue>['loadOptions'] =
    async (_inputValue, _pagination, callback) => {
      if (!baseContentTypeId || departmentId === null) {
        callback({
          options: [],
          pagination: { hasMore: false, page: 1 },
        })
        return
      }

      try {
        const approvers = await approverRepository.listRequestableApprovers({
          baseContentTypeId,
          department: departmentId,
          accountCodeIds,
          customFields: [],
          amount,
        })
        callback({
          options: approvers.map(
            (approver) => new RequestableApproverOption(approver)
          ),
          pagination: { hasMore: false, page: 1 },
        })
      } catch {
        callback({
          options: [],
          pagination: { hasMore: false, page: 1 },
        })
      }
    }

  return (
    <AsyncSelect<NextApproverSelectValue>
      cacheOptions={true}
      defaultOptions={true}
      clearCacheOnMenuClose
      loadOptions={loadOptions}
      placeholder={intl.formatMessage(messages.placeholderText)}
      getOptionLabel={(option) => option.displayLabel}
      getOptionValue={(option) => option.id.toString()}
      isDisabled={departmentId === null}
      {...props}
    />
  )
}
